import { useEffect, useState } from 'react'
import '../styles/GameContext.css'

interface GameContextProps {
  phase?: string
  turn?: string
  timer?: number // seconds remaining
  score?: Record<string, number | string> // e.g., { Player1: 3, Player2: 2 }
  message?: string // Custom info message
  urgency?: 'normal' | 'attention' | 'urgent' // Color coding
}

export function GameContext({
  phase,
  turn,
  timer,
  score,
  message,
  urgency = 'normal'
}: GameContextProps) {
  const [timeLeft, setTimeLeft] = useState(timer)

  // Countdown timer
  useEffect(() => {
    if (timer === undefined) return

    setTimeLeft(timer)

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev === undefined || prev <= 0) return 0
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [timer])

  // Don't render if no content
  if (!phase && !turn && timer === undefined && !score && !message) {
    return null
  }

  return (
    <div className={`game-context game-context-${urgency}`}>
      {/* Phase Indicator */}
      {phase && (
        <div className="game-context-item game-context-phase">
          <span className="game-context-label">Phase:</span>
          <span className="game-context-value">{phase}</span>
        </div>
      )}

      {/* Turn Indicator */}
      {turn && (
        <div className="game-context-item game-context-turn">
          <span className="game-context-label">Turn:</span>
          <span className="game-context-value">{turn}</span>
        </div>
      )}

      {/* Timer */}
      {timer !== undefined && (
        <div className="game-context-item game-context-timer">
          <span className="game-context-icon">⏱️</span>
          <span className={`game-context-value ${(timeLeft ?? 0) <= 10 ? 'timer-urgent' : ''}`}>
            {formatTime(timeLeft ?? 0)}
          </span>
        </div>
      )}

      {/* Score */}
      {score && Object.keys(score).length > 0 && (
        <div className="game-context-item game-context-score">
          {Object.entries(score).map(([player, points]) => (
            <div key={player} className="score-entry">
              <span className="score-player">{player}:</span>
              <span className="score-value">{points}</span>
            </div>
          ))}
        </div>
      )}

      {/* Custom Message */}
      {message && (
        <div className="game-context-item game-context-message">
          <span className="game-context-value">{message}</span>
        </div>
      )}
    </div>
  )
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
